import { useCallback, useState } from 'react';
import { enviarLandingLead } from '../../data/landingLeadsRepository';
import { buildWhatsAppUrl } from '../../domain/landingContent';

export interface LandingLeadFormData {
  nome: string;
  restaurante: string;
  cidade: string;
  whatsapp: string;
  numTelas: string;
  mensagem: string;
  lgpd: boolean;
}

const INITIAL: LandingLeadFormData = {
  nome: '',
  restaurante: '',
  cidade: '',
  whatsapp: '',
  numTelas: '',
  mensagem: '',
  lgpd: false,
};

function normalizarWhatsapp(valor: string): string {
  return valor.replace(/\D/g, '');
}

function validarWhatsapp(digits: string): boolean {
  return digits.length >= 10 && digits.length <= 13;
}

function montarMensagemWhatsApp(d: LandingLeadFormData): string {
  const linhas = [
    'Olá! Preenchi o formulário no site do DS Espetos Grill.',
    '',
    `Nome: ${d.nome}`,
    d.restaurante ? `Restaurante: ${d.restaurante}` : null,
    d.cidade ? `Cidade: ${d.cidade}` : null,
    `WhatsApp: ${d.whatsapp}`,
    d.numTelas ? `Nº de telas: ${d.numTelas}` : null,
    d.mensagem ? `Mensagem: ${d.mensagem}` : null,
  ].filter(Boolean);
  return linhas.join('\n');
}

export function useLandingLeadForm() {
  const [dados, setDados] = useState<LandingLeadFormData>(INITIAL);
  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [whatsappFallbackUrl, setWhatsappFallbackUrl] = useState<string | null>(null);

  const atualizar = useCallback(
    <K extends keyof LandingLeadFormData>(campo: K, valor: LandingLeadFormData[K]) => {
      setDados((prev) => ({ ...prev, [campo]: valor }));
      setErro(null);
      setWhatsappFallbackUrl(null);
    },
    [],
  );

  const validar = useCallback((): string | null => {
    if (!dados.nome.trim()) return 'Informe seu nome.';
    const wa = normalizarWhatsapp(dados.whatsapp);
    if (!validarWhatsapp(wa)) return 'Informe um WhatsApp válido com DDD.';
    if (!dados.lgpd) return 'Aceite o uso dos dados para continuar.';
    return null;
  }, [dados]);

  const enviar = useCallback(async () => {
    const msgErro = validar();
    if (msgErro) {
      setErro(msgErro);
      return;
    }

    setLoading(true);
    setErro(null);

    const payload = {
      nome: dados.nome.trim(),
      restaurante: dados.restaurante.trim() || undefined,
      cidade: dados.cidade.trim() || undefined,
      whatsapp: normalizarWhatsapp(dados.whatsapp),
      num_telas: dados.numTelas ? parseInt(dados.numTelas, 10) : undefined,
      mensagem: dados.mensagem.trim() || undefined,
    };

    try {
      await enviarLandingLead(payload);
      setSucesso(true);
      setDados(INITIAL);
    } catch {
      setErro('Não foi possível enviar agora. Fale com a gente pelo WhatsApp.');
      setWhatsappFallbackUrl(buildWhatsAppUrl(montarMensagemWhatsApp(dados)));
    } finally {
      setLoading(false);
    }
  }, [dados, validar]);

  return { dados, atualizar, enviar, loading, sucesso, erro, whatsappFallbackUrl, setSucesso };
}
